import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { difficultiesLabels } from 'app/frontend/types/custom/Difficulty'
import RaidPlan from 'app/frontend/types/custom/RaidPlan'
import { usePageInfo, useRaidPlans } from 'app/frontend/base/config/queries'
import { RaidPlanOwner } from 'app/frontend/base/api/raid_plans'
import RaidPlanCandidatePicker from './RaidPlanCandidatePicker'
import { ClipboardPaste, CopyIcon, Loader2 } from 'lucide-react'

type CopyFromPlanPopoverProps = {
  owner: RaidPlanOwner
  planListPathPrefix: string
  currentPlan: RaidPlan
  buttonLabel: string
  emptyLabel: string
  onSelectPlan: (plan: RaidPlan) => void | Promise<void>
  onImportFromExportString: () => void
}

const CopyFromPlanPopover = ({
  owner,
  planListPathPrefix,
  currentPlan,
  buttonLabel,
  emptyLabel,
  onSelectPlan,
  onImportFromExportString,
}: CopyFromPlanPopoverProps) => {
  const { t } = useTranslation()
  const {
    data: { currentSeason },
  } = usePageInfo()
  const [open, setOpen] = useState(false)
  const [isCopying, setIsCopying] = useState(false)
  const { data: teamPlans } = useRaidPlans(owner, currentSeason?.id, open)
  const { data: personalPlans } = useRaidPlans(
    owner.ownerType === 'team' ? { ownerType: 'user' } : undefined,
    currentSeason?.id,
    open,
  )

  const candidatePlans = useMemo(() => {
    return [...(teamPlans ?? []), ...(personalPlans ?? [])]
      .filter(
        (plan) =>
          plan.encounterId === currentPlan.encounterId &&
          !(
            plan.id === currentPlan.id &&
            plan.ownerType === currentPlan.ownerType
          ),
      )
      .sort((left, right) => {
        if (left.difficulty !== right.difficulty) {
          if (left.difficulty === currentPlan.difficulty) return -1
          if (right.difficulty === currentPlan.difficulty) return 1
          return difficultiesLabels[left.difficulty].localeCompare(
            difficultiesLabels[right.difficulty],
          )
        }
        return left.name.localeCompare(right.name)
      })
  }, [
    currentPlan.difficulty,
    currentPlan.encounterId,
    currentPlan.id,
    currentPlan.ownerType,
    teamPlans,
    personalPlans,
  ])

  const handleImportFromExportString = () => {
    setOpen(false)
    onImportFromExportString()
  }

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        if (isCopying) return
        setOpen(nextOpen)
      }}
    >
      <PopoverTrigger asChild>
        <Button
          className='h-32 w-52 flex flex-col gap-3 items-center justify-center'
          variant='outline'
        >
          <CopyIcon className='w-8 h-8' />
          {buttonLabel}
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-fit max-w-[90vw]'>
        <div className='relative space-y-2'>
          {isCopying ? (
            <div className='absolute inset-0 z-10 flex items-center justify-center rounded-md bg-background/70'>
              <Loader2 className='h-5 w-5 animate-spin' />
            </div>
          ) : null}
          <RaidPlanCandidatePicker
            plans={candidatePlans}
            warningDifficulty={currentPlan.difficulty}
            showOwnerBadge
            teamPath={planListPathPrefix}
            onSelect={async (plan) => {
              if (isCopying) return
              setIsCopying(true)
              try {
                await onSelectPlan(plan)
                setOpen(false)
              } finally {
                setIsCopying(false)
              }
            }}
            emptyLabel={emptyLabel}
          />
          <Button
            type='button'
            variant='outline'
            className='w-full gap-2'
            disabled={isCopying}
            onClick={handleImportFromExportString}
          >
            <ClipboardPaste className='h-4 w-4' />
            {t('dashboard.team.plans.onboarding.import-from-export-string')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default CopyFromPlanPopover
